import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Typography,
  Space,
  Statistic,
  Alert,
  Modal,
  Upload,
  Select,
  Table,
  Tag,
  Divider,
  Spin,
  InputNumber
} from 'antd';
import { 
  ArrowLeftOutlined, 
  CreditCardOutlined,
  CheckCircleOutlined,
  UploadOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import dayjs from 'dayjs';
import formatCurrency from '../../utils/formatCurrency';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AccountTopUp = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [accountInfo, setAccountInfo] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [topUpRequests, setTopUpRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentProof, setPaymentProof] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchAccountInfo();
    fetchTopUpRequests();
  }, []);

  const fetchAccountInfo = async () => {
    try {
      setLoadingAccount(true);
      const response = await axios.get('/client/balance');
      setAccountInfo(response.data);
    } catch (error) {
      console.error('Error fetching account info:', error);
      message.error('Failed to load account information');
      // Set default values to prevent undefined errors
      setAccountInfo({
        topUpBalance: 0,
        creditLimit: 0,
        creditUsed: 0,
        totalAvailableBalance: 0,
        availableCredit: 0,
        currency: 'USD'
      });
    } finally {
      setLoadingAccount(false);
    }
  };

  const fetchTopUpRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await axios.get('/client/topup-requests');
      // Backend returns { requests: [...], pagination: {...} }
      setTopUpRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching top-up requests:', error);
      message.error('Failed to load top-up requests');
      setTopUpRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);
      // Re-validate immediately before submitting in case the user reopened
      // the confirm modal without going through handleConfirmSubmit again.
      const values = await form.validateFields();
      const amount = values.amount;
      const notes = values.notes || '';
      // The Form is the single source of truth — fall back to local state for
      // backward compatibility but prefer values from validateFields() so a
      // stale useState ("Retry" path) cannot leak through.
      const submitPaymentMethod = values.paymentMethod || paymentMethod;
      const submitPaymentReference =
        values.paymentReference !== undefined
          ? values.paymentReference
          : paymentReference;
      const formData = new FormData();
      formData.append('amount', amount);
      formData.append('paymentMethod', submitPaymentMethod);
      formData.append('paymentReference', submitPaymentReference || '');
      formData.append('notes', notes);
      if (paymentProof) {
        formData.append('paymentProof', paymentProof);
      }
      const response = await axios.post('/client/topup-request', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.data) {
        message.success('Top-up request submitted successfully! It will be reviewed by our team.');
        setConfirmModalVisible(false);
        form.resetFields();
        setPaymentReference('');
        setPaymentProof(null);
        navigate('/client/dashboard');
      }
    } catch (error) {
      if (error?.errorFields) {
        // antd Form validation already highlighted the fields; no extra toast.
        return;
      }
      console.error('Top-up request submission failed:', error);
      message.error(error.response?.data?.message || 'Failed to submit top-up request');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      await form.validateFields();
      setConfirmModalVisible(true);
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      const isPDF = file.type === 'application/pdf';
      
      if (!isImage && !isPDF) {
        message.error('Only image files (JPG, PNG, etc.) and PDF files are supported');
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('File must be smaller than 10MB');
        return false;
      }

      setFileList([file]);
      setPaymentProof(file);
      return false;
    },
    fileList,
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
      processing: 'blue'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ClockCircleOutlined />;
      case 'approved':
        return <CheckCircleOutlined />;
      case 'rejected':
        return <CloseCircleOutlined />;
      case 'processing':
        return <ReloadOutlined spin />;
      default:
        return null;
    }
  };

  const topUpRequestColumns = [
    {
      title: 'Request ID',
      key: 'requestId',
      render: (_, record) => (
        <Text code>{record.requestNumber || record.requestId || record._id || 'N/A'}</Text>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatCurrency(amount, accountInfo?.currency || 'USD'),
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method) => (method || '').replace('_', ' ').toUpperCase(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag icon={getStatusIcon(status)} color={getStatusColor(status)}>
          {(status || '').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? dayjs(date).format('MMM DD, YYYY HH:mm') : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status && record.status === 'rejected' && (
            <Button
              size="small"
              type="primary"
              ghost
              onClick={() => {
                const nextMethod = record.paymentMethod || 'bank_transfer';
                const nextRef = record.paymentReference || '';
                form.setFieldsValue({
                  amount: record.amount || 0,
                  paymentMethod: nextMethod,
                  paymentReference: nextRef,
                  notes: record.notes || ''
                });
                // Keep the local state mirrors in sync so submitting straight
                // away picks up the retry values rather than the previous ones.
                setPaymentMethod(nextMethod);
                setPaymentReference(nextRef);
              }}
            >
              Retry
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Alert
        type="warning"
        showIcon
        message="Top-up is not available yet"
        description="Online payment gateway integration is pending. Please contact support to top up your account."
        style={{ marginBottom: 24 }}
      />
      <Card 
        title={
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/client/dashboard')}
            >
              Back
            </Button>
            <CreditCardOutlined />
            <span>Add Credit Limit</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >

        {/* Current Account Status */}
        <div style={{ marginBottom: 32 }}>
          <Title level={5}>Current Credit Status</Title>
          {loadingAccount ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>Loading account information...</div>
            </div>
          ) : (
            <Row gutter={24}>
              <Col span={8}>
                <Statistic
                  title="Available Credit Limit"
                  value={(accountInfo?.topUpBalance || 0) + (accountInfo?.creditLimit || 0) - (accountInfo?.creditUsed || 0)}
                  prefix="$"
                  valueStyle={{ color: '#722ed1', fontSize: '22px', fontWeight: 'bold' }}
                />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  <span style={{ marginRight: 12 }}>Prepaid: {formatCurrency(accountInfo?.topUpBalance, accountInfo?.currency)}</span>
                  <span style={{ marginRight: 12 }}>Credit Line: {formatCurrency(accountInfo?.creditLimit, accountInfo?.currency)}</span>
                  <span>Used: {formatCurrency(accountInfo?.creditUsed, accountInfo?.currency)}</span>
                </div>
              </Col>
              <Col span={8}>
                <Statistic
                  title="Prepaid Credit"
                  value={accountInfo?.topUpBalance || 0}
                  prefix="$"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Credit Used"
                  value={accountInfo?.creditUsed || 0}
                  prefix="$"
                  valueStyle={{ color: '#fa8c16' }}
                />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Limit: {formatCurrency(accountInfo?.creditLimit, accountInfo?.currency)}
                </div>
              </Col>
            </Row>
          )}
        </div>

        <Divider />

        {/* Top-up Request Form */}
        <Row gutter={24}>
          <Col span={12}>
            <Title level={5}>Submit Top-up Request</Title>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
              <Form.Item
                name="amount"
                label="Top-up Amount"
                rules={[
                  { required: true, message: 'Please enter top-up amount' },
                  { type: 'number', min: 1, message: 'Amount must be greater than 0' }
                ]}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  prefix="$"
                  placeholder="Enter amount to top-up"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="paymentMethod"
                label="Payment Method"
                rules={[{ required: true, message: 'Please select payment method' }]}
              >
                {/* Single source of truth: antd Form. Mirror to local state for
                    submit handler convenience via onChange. */}
                <Select
                  placeholder="Select payment method"
                  size="large"
                  onChange={(value) => setPaymentMethod(value)}
                >
                  <Option value="bank_transfer">Bank Transfer</Option>
                  <Option value="wire_transfer">Wire Transfer</Option>
                  <Option value="check">Check</Option>
                  <Option value="online_payment">Online Payment</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="paymentReference"
                label="Payment Reference"
                rules={[{ required: true, message: 'Please enter payment reference' }]}
              >
                <Input
                  placeholder="Transaction ID, Reference Number, etc."
                  size="large"
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </Form.Item>

              <Form.Item
                name="notes"
                label="Additional Notes (Optional)"
              >
                <TextArea 
                  rows={3} 
                  placeholder="Any additional information about the payment..."
                  maxLength={500}
                />
              </Form.Item>

              <Form.Item label="Payment Proof" required>
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />} size="large">
                    Select Payment Proof File
                  </Button>
                </Upload>
                <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                  Supported formats: JPG, PNG, PDF (max 10MB)
                </div>
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  onClick={handleConfirmSubmit}
                  loading={submitLoading}
                  disabled={fileList.length === 0}
                  size="large"
                  block
                >
                  Submit Top-up Request
                </Button>
              </Form.Item>
            </Form>
          </Col>

          <Col span={12}>
            <Title level={5}>Top-up Instructions</Title>
            <Alert
              message="How to Top-up Your Account"
              description={
                <div>
                  <ol style={{ paddingLeft: 20, margin: 0 }}>
                    <li>Make payment using your preferred method</li>
                    <li>Upload clear payment proof (receipt/screenshot)</li>
                    <li>Fill in payment reference number</li>
                    <li>Submit request and wait for admin approval</li>
                    <li>Funds will be added to your account after approval</li>
                  </ol>
                  <div style={{ marginTop: 12, padding: 12, background: '#f6f6f6', borderRadius: 6 }}>
                    <strong>Bank Details:</strong><br/>
                    Bank: Example Bank<br/>
                    Account: 123-456-789<br/>
                    Swift: EXAMPLEXXX
                  </div>
                </div>
              }
              type="info"
              showIcon
            />
          </Col>
        </Row>
      </Card>

      {/* Top-up Request History */}
      <Card 
        title="Top-up Request History"
        extra={
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchTopUpRequests}
            loading={loadingRequests}
          >
            Refresh
          </Button>
        }
      >
        <Table
          columns={topUpRequestColumns}
          dataSource={Array.isArray(topUpRequests) ? topUpRequests : []}
          rowKey={(record, index) =>
            record._id ||
            record.requestId ||
            record.requestNumber ||
            `topup-${index}`
          }
          loading={loadingRequests}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Confirmation Modal */}
      <Modal
        title="Confirm Top-up Request"
        open={confirmModalVisible}
        onCancel={() => setConfirmModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            loading={submitLoading}
            onClick={() => form.submit()}
          >
            Confirm & Submit
          </Button>,
        ]}
        width={600}
      >
        {form.getFieldsValue() && Object.keys(form.getFieldsValue()).length > 0 && (
          <div>
            <Title level={5}>Request Details</Title>
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Amount:</Text><br/>
                  <Text>{formatCurrency(form.getFieldValue('amount') || 0, accountInfo?.currency || 'USD')}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Payment Method:</Text><br/>
                  <Text>{(form.getFieldValue('paymentMethod') || '').replace('_', ' ').toUpperCase()}</Text>
                </Col>
                <Col span={12} style={{ marginTop: 16 }}>
                  <Text strong>Payment Reference:</Text><br/>
                  <Text>{form.getFieldValue('paymentReference') || 'N/A'}</Text>
                </Col>
                <Col span={12} style={{ marginTop: 16 }}>
                  <Text strong>Payment Proof:</Text><br/>
                  <Text>{fileList[0]?.name || 'No file selected'}</Text>
                </Col>
              </Row>
            </div>
            <Alert
              message="Please Note"
              description="Your top-up request will be submitted to admin for approval. You will receive a notification once it's processed."
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AccountTopUp;